/* eslint-disable @typescript-eslint/no-explicit-any */
import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import {
  AvailabilityProduct,
  FiltersType,
  FormData,
  Sale,
  SalePayload,
  UpdateSale,
  UpdateSalePayload,
} from '~/shared/api/services/marketing/types'
import { SelectOption } from '~/shared/ui'
import { Product } from '~/shared/api'
import { DISCOUNT_VARIANT_TYPES, handleAxiosError } from '~/shared/lib'
import { MarketingService } from '~/shared/api/services/marketing'
import { store } from '~/app/store'

export class DiscountStore {
  requestSettings = {
    limit: 15,
    offset: 0,
  }

  filters: FiltersType = {
    productId: null,
    status: { value: 'All', label: 'Все' },
    categoryId: null,
    date: [],
    storeIds: [],
  }

  variantTypeId = 1

  sales: Sale[] = []

  productsForFilter: Product[] = []

  q = ''

  formQ = ''

  states = [
    { value: 'All', label: 'Все' },
    { value: 'Active', label: 'Активно' },
    { value: 'Inactive', label: 'Неактивно' },
    { value: 'Finished', label: 'Завершено' },
  ]

  formData: FormData = {
    dateEnd: null,
    dateStart: null,
    sale: null,
    timeStart: null,
    timeEnd: null,
    maxCountUse: null,
    productAvailabilityId: null,
    storeId: null,
    categoryId: null,
  }

  availabilityProductsForForm: AvailabilityProduct[] = []

  subContentOpens: string[] = []

  updateSales: UpdateSale[] = [] // для открывающейся формы редактирования скидки

  totalPagesSales = 0

  constructor() {
    makeAutoObservable(this)
  }

  setIdsSubContentOpens = (id: string) => {
    runInAction(() => {
      this.subContentOpens = this.subContentOpens.includes(id)
        ? this.subContentOpens.filter((e) => e !== id)
        : [...this.subContentOpens, id]
    })
  }

  updateSale = async (id: string, path: 'product' | 'category') => {
    try {
      const sale = this.updateSales.find((e) => e.id === id)
      if (sale) {
        const payload: UpdateSalePayload = {
          id: sale.id,
          dateStart: sale.dateStart || null,
          dateEnd: sale.dateEnd || null,
          maxCountUse:
            sale.maxCountUse === '' ? null : (sale.maxCountUse ?? null),
          timeStart: sale.timeStart || null,
          timeEnd: sale.timeEnd || null,
        }
        const { data } = await MarketingService.updateSale(payload, path)
        this.sales = this.sales.map((e) => (e.id === id ? { ...data } : e)) // отображение списка скидок
        this.updateSales = this.updateSales.map((e) =>
          e.id === id ? { ...e, state: data.state } : e,
        ) // для выпадашек
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    }
  }

  getProductsForFilter = async () => {
    try {
      store.marketingStore.activeLoader('products')
      const search = this.q
        ? {
            params: {
              q: this.q,
            },
          }
        : {}
      const { data } = await MarketingService.getProductsForFilter(search)

      runInAction(() => {
        this.productsForFilter = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw error.response?.data.error
      }
    } finally {
      runInAction(() => {
        store.marketingStore.closeLoader('products')
      })
    }
  }

  createSale = async () => {
    try {
      store.marketingStore.activeLoader('createSale')

      const variant = DISCOUNT_VARIANT_TYPES.find(
        (e) => e.id === this.variantTypeId,
      )
      if (variant?.path) {
        const saleValue = this.formData.sale as number | string | null
        const maxCountUseValue = this.formData.maxCountUse as
          | number
          | string
          | null

        const dataForServer: SalePayload = {
          dateStart: (this.formData.dateStart as Date | string | null) || null,
          dateEnd: (this.formData.dateEnd as Date | string | null) || null,
          sale:
            saleValue === '' || saleValue == null ? null : Number(saleValue),
          timeStart: this.formData.timeStart || null,
          timeEnd: this.formData.timeEnd || null,
          maxCountUse:
            maxCountUseValue === '' || maxCountUseValue == null
              ? null
              : Number(maxCountUseValue),
          productAvailabilityId:
            this.formData.productAvailabilityId?.value || null,
          storeId: this.formData.storeId?.value || null,
          categoryId: this.formData.categoryId?.value || null,
        }

        await MarketingService.createSale(
          dataForServer,

          variant.path as 'product' | 'category' | 'all',
        )
        this.requestSettings = { offset: 0, limit: 15 }
        await this.getSales()
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      store.marketingStore.closeLoader('createSale')
    }
  }

  deleteSale = async (id: string, path: 'product' | 'category') => {
    try {
      store.marketingStore.activeLoader('deleteSale')
      await MarketingService.deleteSale(id, path)
      runInAction(() => {
        this.sales = this.sales.filter((sale) => sale.id !== id)
        this.updateSales = this.updateSales.filter((sale) => sale.id !== id)
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      store.marketingStore.closeLoader('deleteSale')
    }
  }

  setUpdateSales = ({
    id,
    name,
    value,
  }: {
    id: string
    name: string
    value: string | Date | null
  }) => {
    runInAction(() => {
      this.updateSales = this.updateSales.map((sale) =>
        sale.id === id ? { ...sale, [name]: value } : sale,
      )
    })
  }

  getProductsForForm = async () => {
    try {
      store.marketingStore.activeLoader('products')
      const search = this.formQ
        ? {
            params: {
              q: this.formQ,
              storeId: this.formData.storeId?.value,
            },
          }
        : { params: { storeId: this.formData.storeId?.value } }
      const { data } = await MarketingService.getProductsForForm(search)

      runInAction(() => {
        this.availabilityProductsForForm = data
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        throw error.response?.data.error
      }
    } finally {
      runInAction(() => {
        store.marketingStore.closeLoader('products')
      })
    }
  }

  getSales = async (action?: 'loadMoreSales') => {
    try {
      const params: { offset: number; limit: number; [key: string]: any } = {
        offset: action ? this.requestSettings.offset : 0,
        limit: this.requestSettings.limit,
      }
      store.marketingStore.activeLoader(action ? 'loadMoreSales' : 'sales')
      const filterKeys = Object.keys(this.filters) as (keyof FiltersType)[]
      filterKeys.forEach((filterKey) => {
        if (filterKey === 'status') {
          if (
            this.filters.status?.value &&
            this.filters.status.value !== 'All'
          ) {
            params[filterKey] = this.filters.status.value
          }
        } else if (filterKey === 'storeIds' && this.filters.storeIds.length) {
          params['storeIds[]'] = this.filters.storeIds.map(({ value }) => value)
        } else if (filterKey === 'date' && this.filters.date.length) {
          const [dateStart] = this.filters.date
          params.dateStart = dateStart
          params.dateEnd = this.filters.date[this.filters.date.length - 1]
        } else if (filterKey === 'productId' || filterKey === 'categoryId') {
          const selectedFilter = this.filters[filterKey]
          if (selectedFilter) {
            params[filterKey] = selectedFilter.value
          }
        }
      })

      const resp = await MarketingService.getSales({
        params,
      })
      const { data } = resp
      const { totalPages }: { totalPages: number } = resp as any

      runInAction(() => {
        this.sales = action ? [...this.sales, ...data.sales] : data.sales
        const emptyString: unknown = ''
        this.updateSales = this.sales.map((e) => ({
          dateEnd: e.dateEnd ? e.dateEnd : (emptyString as Date),
          dateStart: e.dateStart ? e.dateStart : (emptyString as Date),
          maxCountUse:
            typeof e.maxCountUse === 'number' && e.maxCountUse >= 0
              ? String(e.maxCountUse)
              : '',
          timeEnd: null,
          timeStart: null,
          id: e.id,
          state: e.state,
          storeId: e.storeId,
        }))

        this.requestSettings = {
          ...this.requestSettings,
          offset: this.requestSettings.offset + this.requestSettings.limit,
        }
        this.totalPagesSales = totalPages
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        const responseData = error.response?.data
        let responseMessage: string | null = null

        if (typeof responseData === 'string') {
          responseMessage = responseData
        } else if (typeof responseData?.error === 'string') {
          responseMessage = responseData.error
        }

        throw new Error(responseMessage || handleAxiosError(error))
      }
    }
    store.marketingStore.closeLoader(action ? 'loadMoreSales' : 'sales')
  }

  setFilters = (
    name: keyof FiltersType,
    value: FiltersType[keyof FiltersType],
  ) => {
    this.filters = { ...this.filters, [name]: value }
    this.requestSettings.offset = 0
  }

  setFormData = (
    name: keyof FormData,
    value: string | SelectOption | number | Date | null,
  ) => {
    this.formData = { ...this.formData, [name]: value }
  }

  setVariantType = (value: number) => {
    runInAction(() => {
      this.variantTypeId = value
      this.formData = {
        ...this.formData,
        categoryId: null,
        productAvailabilityId: null,
      }
    })
  }

  clearFormData = () => {
    this.formData = {
      dateEnd: null,
      dateStart: null,
      sale: null,
      timeStart: null,
      timeEnd: null,
      maxCountUse: null,
      productAvailabilityId: null,
      storeId: null,
      categoryId: null,
    }
  }

  clearFilters = () => {
    this.filters = {
      productId: null,
      status: { value: 'All', label: 'Все' },
      categoryId: null,
      date: [],
      storeIds: [],
    }
    this.requestSettings.offset = 0
  }

  clearAvailabilityProductsForForm() {
    this.availabilityProductsForForm = []
  }
}

export const discountStore = new DiscountStore()
